import React, { useEffect, useMemo, useState } from "react";
import {
	Alert,
	Box,
	Button,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControl,
	FormControlLabel,
	IconButton,
	InputLabel,
	MenuItem,
	Paper,
	Select,
	Switch,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import AccountingOrgBar from "./AccountingOrgBar";
import { canLoadAccountingData } from "../useAccountingScope";
import { usePaymentMethods } from "services/hooks/rentals";

const PaymentMethodMappingsTab = ({
	requiresOrganizationSelection,
	sessionOrganization,
	organizationId,
	setOrganizationId,
	fetchAccounts,
	fetchPaymentMethodMappings,
	savePaymentMethodMapping,
	updatePaymentMethodMapping,
	deletePaymentMethodMapping,
	isSubmitting,
}) => {
	const { paymentMethods, isLoadingPaymentMethods } = usePaymentMethods();
	const [mappings, setMappings] = useState([]);
	const [accounts, setAccounts] = useState([]);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState("");
	const [message, setMessage] = useState("");
	const [dialogOpen, setDialogOpen] = useState(false);
	const [editingId, setEditingId] = useState(null);
	const [form, setForm] = useState({
		payment_method_id: "",
		account_id: "",
		is_active: true,
	});

	const canLoad = canLoadAccountingData(requiresOrganizationSelection, organizationId);

	const load = async () => {
		if (!canLoad) {
			setMappings([]);
			setAccounts([]);
			return;
		}
		setLoading(true);
		setError("");
		const [mappingsResult, accountsResult] = await Promise.all([
			fetchPaymentMethodMappings(organizationId),
			fetchAccounts(organizationId),
		]);
		if (mappingsResult.success) setMappings(mappingsResult.mappings);
		else setError(mappingsResult.message);
		if (accountsResult.success) setAccounts(accountsResult.accounts);
		setLoading(false);
	};

	useEffect(() => {
		load();
	}, [organizationId, requiresOrganizationSelection]);

	const postableAccounts = useMemo(
		() => accounts.filter((a) => a.is_postable !== false && a.is_active !== false),
		[accounts]
	);

	const mappedMethodIds = useMemo(
		() => new Set(mappings.map((m) => m.related_payment_method)),
		[mappings]
	);

	const availableMethods = useMemo(() => {
		const list = Array.isArray(paymentMethods) ? paymentMethods : [];
		if (editingId) return list;
		return list.filter((m) => !mappedMethodIds.has(m.id));
	}, [paymentMethods, mappedMethodIds, editingId]);

	const openCreate = () => {
		setEditingId(null);
		setForm({
			payment_method_id: availableMethods[0]?.id ?? "",
			account_id: "",
			is_active: true,
		});
		setMessage("");
		setDialogOpen(true);
	};

	const openEdit = (row) => {
		setEditingId(row.id);
		setForm({
			payment_method_id: row.related_payment_method,
			account_id: row.related_account,
			is_active: row.is_active !== false,
		});
		setMessage("");
		setDialogOpen(true);
	};

	const handleSubmit = async () => {
		if (!form.payment_method_id || !form.account_id) {
			setMessage("Payment method and account are required.");
			return;
		}
		setMessage("");
		let result;
		if (editingId) {
			result = await updatePaymentMethodMapping(editingId, {
				account_id: Number(form.account_id),
				is_active: form.is_active,
			});
		} else {
			const payload = {
				payment_method_id: Number(form.payment_method_id),
				account_id: Number(form.account_id),
				is_active: form.is_active,
			};
			if (requiresOrganizationSelection) {
				payload.organization_id = organizationId;
			}
			result = await savePaymentMethodMapping(payload);
		}
		if (result.success) {
			setDialogOpen(false);
			load();
		} else {
			setMessage(result.message);
		}
	};

	const handleDeactivate = async (row) => {
		const result = await deletePaymentMethodMapping(row.id);
		if (result.success) load();
		else setError(result.message);
	};

	return (
		<Box>
			<Box display="flex" gap={2} flexWrap="wrap" alignItems="center" mb={2}>
				<AccountingOrgBar
					requiresOrganizationSelection={requiresOrganizationSelection}
					organizationId={organizationId}
					onOrganizationChange={setOrganizationId}
					sessionOrganization={sessionOrganization}
				/>
				<Button
					variant="contained"
					startIcon={<AddIcon />}
					onClick={openCreate}
					disabled={!canLoad || isSubmitting || isLoadingPaymentMethods}
				>
					Add mapping
				</Button>
			</Box>

			<Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
				Map each payment method (Cash, MTN, Airtel, Bank, Airtel Merchant) to a chart account.
				Posting template lines with &quot;Use payment method&quot; will post to the mapped account at runtime.
			</Typography>

			{error && (
				<Alert severity="error" sx={{ mb: 2 }}>
					{error}
				</Alert>
			)}
			{message && !dialogOpen && (
				<Alert severity="error" sx={{ mb: 2 }} onClose={() => setMessage("")}>
					{message}
				</Alert>
			)}

			{!canLoad ? (
				<Typography color="text.secondary">Select an organisation to manage mappings.</Typography>
			) : loading || isLoadingPaymentMethods ? (
				<Box display="flex" justifyContent="center" py={4}>
					<CircularProgress />
				</Box>
			) : (
				<TableContainer component={Paper}>
					<Table size="small">
						<TableHead>
							<TableRow>
								<TableCell>Payment method</TableCell>
								<TableCell>Account</TableCell>
								<TableCell>Active</TableCell>
								<TableCell align="right">Actions</TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							{mappings.length === 0 ? (
								<TableRow>
									<TableCell colSpan={4} align="center">
										No payment method mappings yet.
									</TableCell>
								</TableRow>
							) : (
								mappings.map((row) => (
									<TableRow key={row.id} hover>
										<TableCell>{row.payment_method_name || row.related_payment_method}</TableCell>
										<TableCell sx={{ fontFamily: "monospace" }}>
											{row.account_code} — {row.account_name}
										</TableCell>
										<TableCell>{row.is_active ? "Yes" : "No"}</TableCell>
										<TableCell align="right">
											<IconButton size="small" onClick={() => openEdit(row)} disabled={isSubmitting}>
												<EditIcon fontSize="small" />
											</IconButton>
											{row.is_active !== false && (
												<IconButton
													size="small"
													onClick={() => handleDeactivate(row)}
													disabled={isSubmitting}
													title="Deactivate mapping"
												>
													<DeleteIcon fontSize="small" />
												</IconButton>
											)}
										</TableCell>
									</TableRow>
								))
							)}
						</TableBody>
					</Table>
				</TableContainer>
			)}

			<Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
				<DialogTitle>{editingId ? "Edit mapping" : "Map payment method"}</DialogTitle>
				<DialogContent>
					<Box display="flex" flexDirection="column" gap={2} mt={1}>
						{message && <Alert severity="error">{message}</Alert>}
						<FormControl fullWidth disabled={Boolean(editingId)}>
							<InputLabel>Payment method</InputLabel>
							<Select
								label="Payment method"
								value={form.payment_method_id}
								onChange={(e) => setForm((f) => ({ ...f, payment_method_id: e.target.value }))}
							>
								{(editingId
									? Array.isArray(paymentMethods)
										? paymentMethods
										: []
									: availableMethods
								).map((method) => (
									<MenuItem key={method.id} value={method.id}>
										{method.name}
									</MenuItem>
								))}
							</Select>
						</FormControl>
						<FormControl fullWidth>
							<InputLabel>Chart account</InputLabel>
							<Select
								label="Chart account"
								value={form.account_id}
								onChange={(e) => setForm((f) => ({ ...f, account_id: e.target.value }))}
							>
								{postableAccounts.map((a) => (
									<MenuItem key={a.id} value={a.id}>
										{a.code} — {a.name}
									</MenuItem>
								))}
							</Select>
						</FormControl>
						{editingId && (
							<FormControlLabel
								control={
									<Switch
										checked={form.is_active}
										onChange={(e) => setForm((f) => ({ ...f, is_active: e.target.checked }))}
									/>
								}
								label="Active"
							/>
						)}
					</Box>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setDialogOpen(false)}>Cancel</Button>
					<Button variant="contained" onClick={handleSubmit} disabled={isSubmitting}>
						{isSubmitting ? "Saving…" : "Save"}
					</Button>
				</DialogActions>
			</Dialog>
		</Box>
	);
};

export default PaymentMethodMappingsTab;
